<template>
    <div class="help">
        <b-container>
            <div class="box mb-5" v-if="!onlyAccess">
                <h2 class="mb-5"><i class="far fa-question-square mr-3"></i>Help, FAQs and Videos</h2>


                <div>
                    <h5>How to Register for a Conference with a Credit Card</h5>

                    <ol>
                        <li>Start with the <strong>Conference Code</strong> on the homepage of <a href="https://www.registermyconference.com">registermyconference.com</a>. Alternatively, you can start from the <strong>conference link</strong> that was shared with you.</li>
                        <li>Complete the information sections at the top of the registration form.</li>
                        <li>Once you reach the <strong>Payment section</strong> of the form towards the bottom be sure to select the <strong>Credit Card</strong> option first then you can add in your credit card information.</li>
                        <li>When you are finished adding your credit card information you can complete the registration form by clicking the green <strong>Submit Registration</strong> button on the bottom of the form.</li>
                        <li>Watch your e-mail for your registration confirmation.  <strong>Keep this e-email handy, it's your easiest way back to your conference information!</strong></li>
                    </ol>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Register_by_CC.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to Register for a Conference with a Purchase Order (P.O.)</h5>

                    <ol>
                        <li>Start with the <strong>Conference Code</strong> on the homepage of <a href="https://www.registermyconference.com">registermyconference.com</a>. Alternatively, you can start from the <strong>conference link</strong> that was shared with you.</li>
                        <li>Complete the information sections at the top of the registration form.</li>
                        <li>Once you reach the <strong>Payment section</strong> of the form towards the bottom be sure to select the <strong>Purchase Order</strong> option.</li>
                        <li>Next step is to click on the green <strong>Submit Registration</strong> button on the bottom of the form. This button will trigger an alert that informs the conference organizers of your pending status as a purchase order registrant. The button also takes you to the <strong>Purchase Order Generator</strong> where you can complete a form to generate a form to take to your organization. (If you need to come back to this page later you can access the purchase order generator by clicking on the link in the Action Required email you receive.)</li>
                        <li>Complete the form to generate a purchase order to print, copy or email the generated purchase order to your organization.</li>
                    </ol>

                    <blockquote class="ml-4 mb-4">
                        <i class="far fa-exclamation-triangle mr-3"></i><strong>Registration <u>can not</u> be completed until the Purchase Order process has been fulfilled.</strong> It is your responsibility to follow up with this process. Once the purchase order process has been verified you will receive a confirmation email about the completion of your registration.
                    </blockquote>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Register_by_PO.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to Register for a Conference with a Promo/Discount Code</h5>

                    <ol>
                        <li>Start with the <strong>Conference Code</strong> on the homepage of <a href="https://www.registermyconference.com">registermyconference.com</a>. Alternatively, you can start from the <strong>conference link</strong> that was shared with you.</li>
                        <li>Complete the information sections at the top of the registration form.</li>
                        <li>Once you reach the <strong>Payment section</strong> of the form towards the bottom be sure to select the <strong>Purchase Order</strong> option.</li>
                        <li>Once you reach the <strong>Discount Code</strong> section of the form towards the bottom you can add in the promo/discount code to the input field and click on the Apply Discount Code button.</li>
                        <li>If valid a discount will be applied to your registration cost.  If the discount makes the registration free then payment information will no longer be required.</li>
                    </ol>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Register_Promo_Code.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to Access Your Registration Information and Conference</h5>
                    <h6>Once You've Registered</h6>

                    <ol>
                        <li>Click on the <strong>Access Conference Information</strong> button from the confirmation e-mail you received from registration.</li>
                        <li>You'll be auto-magically logged in to your conference.</li>
                        <li>Once inside your personal RegisterMyConference dashboard you'll see a few tabs that include basic conference information,
                            conference session and schedules as well as your registration information.</li>
                    </ol>

                    <blockquote class="ml-4 mb-4">
                        <i class="far fa-exclamation-triangle mr-3"></i>Lost your registration e-mail?  No problem.  From the home page, <a href="https://www.registermyconference.com">registermyconference.com</a>, click on <strong>Access Your Registrations & Records</strong> to recover your registrations.
                    </blockquote>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Access_Registration_Info.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to RSVP for a Session</h5>
                    <h6>Once You've Registered</h6>

                    <ol>
                        <li>Click on the <strong>Access Conference Information</strong> button from the confirmation e-mail you received from registration.</li>
                        <li>Once inside your personal RegisterMyConference dashboard, click on the <strong>Sessions & Schedule</strong> tab.</li>
                        <li>Locate the sessions you'd like to attend and click on the <strong>Plan to Attend</strong> button next to each session. If you’d like to cancel an RSVP click on the <strong>X</strong> button next to the session then click on the OK button in the confirmation message.</li>
                    </ol>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/RSVP_Session.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to Access a Live Session</h5>

                    <ol>
                        <li>Click on the <strong>Access Conference Information</strong> button from the confirmation e-mail you received from registration. You may also receive an email about a session directly from the presenter with information that you can use as a way to access conference information.</li>
                        <li>Once inside your personal RegisterMyConference dashboard, click on the <strong>Sessions & Schedule</strong> tab.</li>
                        <li>Locate the session you'd like to join and click on the green link button next to the <strong>Online Presentation/Meeting</strong> section below the session information.</li>
                    </ol>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Start_Live_Session.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to Access an On-Demand Session</h5>

                    <ol>
                        <li>Click on the <strong>Access Conference Information</strong> button from the confirmation e-mail you received from registration.</li>
                        <li>Once inside your personal RegisterMyConference dashboard, click on the <strong>Sessions & Schedule</strong> tab.</li>
                        <li>Locate the session you'd like to join and click on the green link button next to the <strong>Online Presentation/Meeting</strong> section below the session information.</li>
                    </ol>

                    <blockquote class="ml-4 mb-4">
                        <i class="far fa-exclamation-triangle mr-3"></i>You will not have access to a session until you've marked that you plan to attend, even if that session has already started or finished.  You may need to do that step first.
                    </blockquote>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/On_Demand_Sessions.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5"/>

                <div>
                    <h5>How to Generate a Conference Session Certificate</h5>
                    <h6>(If applicable)</h6>

                    <ol>
                        <li>Click on the <strong>Access Conference Information</strong> button from the confirmation e-mail you received from registration.</li>
                        <li>Once inside your personal RegisterMyConference dashboard, click on the <strong>Sessions & Schedule</strong> tab.</li>
                        <li>If applicable, locate the session that you’ve completed and click on the Take Survey button next to it. The survey must be completed to generate a certificate. Complete the survey and click on the Submit Survey button.</li>
                        <li>Click on the <strong>Print</strong> button next to the <strong>Completion Certificate</strong> section.</li>
                    </ol>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Generate_Certificate.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>

                <hr class="mt-5 mb-5" ref="clockhours" id="clockhours" />

                <div>
                    <h5>How to Receive Professional Development Clock Hours</h5>
                    <h6>(If applicable, for Washington State Educators)</h6>

                    <ol>
                        <li>Click on the <strong>Access Conference Information</strong> button from the confirmation e-mail you received from registration.</li>
                        <li>Locate the <strong>Washington State Approved Clock-Hours</strong> section from the dashboard home and either look up your my247ed.com account or create a new one. If you’ve looked up your my247ed.com account then you will see an updated section on the dashboard home page that includes a list of sessions you’ve RSVP for. If you’ve created a new my247ed.com account then you will need to refresh the dashboard home page to see this same list.</li>
                        <li>Review the list and complete any sessions that you’d like to receive clock-hours for. Notice the message below the list about transferring clock-hours. <strong>The credit transfer feature is only available <u>once</u> per conference</strong> so make sure to complete all sessions that you are wanting to receive clock-hours for. Click on the X button next to any session you’d like to omit from the clock-hours transfer. Once you are ready to transfer click on the green I’m Ready to Transfer button.</li>
                        <li>Confirm that you are ready to transfer by clicking on the OK button in the confirmation alert box. </li>
                        <li>Click on the <strong>Access Your Records On my247ed.com</strong> button to be redirected to your my247ed.com dashboard.</li>
                        <li>Click on the <strong>Enrollments & Records</strong> page then click on the <strong>Conferences</strong> tab.</li>
                        <li>Complete the transfer by clicking the <strong>Complete</strong> button.</li>
                        <li>If applicable, fill out the checkout form and click on the <strong>Checkout</strong> button.</li>
                        <li>Refresh the <strong>Enrollments & Records</strong> page to see the updated green Completed status. You must next complete the Conference Survey in order to receive the clock-hours.</li>
                        <li>Once the survey is completed, click on the <strong>Submit Attendance Log</strong> button then click the <strong>Submit Online</strong> button.</li>
                        <li>Your information will be submitted and reviewed.  Once approved official forms will be made available to you to submit to your district for advancement.</li>
                    </ol>

                    <blockquote class="ml-4 mb-4">
                        <i class="far fa-exclamation-triangle mr-3"></i>THE CREDIT TRANSFER FEATURE IS ONLY AVAILABLE ONCE PER CONFERENCE.  Make sure you are complete with all sessions before transferring to my247ed.com.
                    </blockquote>

                    <b-card class="ml-4">
                        <b-embed type="video" controls>
                            <source src="@/assets/videos/Conf_Clock_Hours.mp4" type="video/mp4">
                        </b-embed>
                    </b-card>
                </div>
            </div>

            <div class="box mb-5" v-if="!onlyAccess">
                <h3 class="mt-4 mb-4"><i class="far fa-headset mr-4"></i>Still need some help? <a href="mailto: support@my247ed.com">Contact us</a>.</h3>
            </div>

            <footer>
                <div class="mt-5">
                    <div><img src="@/assets/logo_white.png" class="logo mt-4"/></div>
                </div>
                <copyright class="mt-2" :hide="true"/>
            </footer>
        </b-container>
    </div>
</template>

<script>
    import Copyright from "../components/Copyright";
    import {mapActions} from 'vuex';

    export default {
        name: 'Home',
        components: {Copyright},
        data: function () {
            return {
                code: '',
                error: null,
                access: false,
                instructions: false,
                help: false,
                login: '',
                processing: false,
                onlyAccess: false
            }
        },
        methods: {
            scrollTo(refName) {
                let element = this.$refs[refName];
                if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                }
            }
        },
        mounted() {
            if (this.$route.query.section) {
                this.scrollTo(this.$route.query.section);
            }
        }
    }
</script>

<style lang="scss">

    @import '~@/assets/scss/_variables.scss';

    .help {
        background: darken($success, 15); /* fallback */
        background: linear-gradient( to bottom, $success, darken($success, 15));

        padding: 3rem 3rem 20rem;
        color: #5c5c5c;
        text-align: center;

        .logo {
            max-width: 350px;
        }

        .box {
            text-align: left;

            padding: 2rem 3rem;
            background: white;
            margin: 5rem;
            border-radius: 0.5rem;
            box-shadow: 0 0 39px 10px rgba(0,0,0,0.2);



            h2 {
                color: $green;
                letter-spacing: -0.08rem;
                margin-bottom: 2rem;
            }

            ol {
                margin-top: 1.5rem;
                margin-bottom: 2rem;
                font-size: 1rem;

                li {
                    margin-bottom: 0.8rem;
                }
            }


        }

        footer {
            margin-top: 7rem;
        }
    }
</style>
